import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { GetUserNotificationListType } from "../../../../Schema/notification.types";
import { setSeenNotifications } from "../../seenNotification/seenNotification";
import { alert } from "../../../../Utils/toast";
import { queryClient } from "../../../store";

export type GetUserNotificationsLoadAction = {
  type: string;
  payload: { page: number; types?: string | null };
};

export type GetUserNotificationsData = {
  data: GetUserNotificationListType;
};

export const GetUserNotifications = new ReduxFetchState<
  GetUserNotificationsData | null,
  { page: number; types?: string | null },
  any
>("getUserNotifications");

export function* watchGetUserNotifications(action: GetUserNotificationsLoadAction) {
  try {
    const lang: "fa" | "en" = yield getCurrentLanguage();

    const data: GetUserNotificationsData = yield axios({
      url: `/api/v1/users/notifications?page=${action.payload.page}${action.payload.types ? `&model=${action.payload.types}` : ""}`,
      method: "GET",
      headers: {
        lang,
      },
    });

    yield put(GetUserNotifications.actions.loadSuccess({ data: data.data }));
    queryClient.removeQueries({ queryKey: ["getAnnouncedNotifications", lang] });
    yield put(setSeenNotifications(true));
  } catch (e: any) {
    alert({
      message: e?.response?.data?.error ?? "Something Went Wrong!",
      type: "error",
    });
    yield put(
      GetUserNotifications.actions.loadSuccess({
        data: {
          data: [],
          current_page: 1,
          last_page: 1,
          models: e?.response?.data?.models,
          total: 0,
        },
      }),
    );
    yield put(GetUserNotifications.actions.loadFailure(e?.response?.data));
  }
}

export function* getUserNotificationsSagas() {
  yield takeEvery(GetUserNotifications.actionTypes.load, watchGetUserNotifications);
}

export function useGetUserNotifications() {
  const { loading, data, ...getUserNotificationsState } = useAppSelector(
    (state) => state.getUserNotifications,
  );
  const dispatch = useAppDispatch();

  const dispatchGetUserNotifications = useCallback(
    (data: { page: number; types?: string | null }) => {
      dispatch(GetUserNotifications.actions.load(data));
    },
    [dispatch],
  );

  return {
    ...getUserNotificationsState,
    dispatchGetUserNotifications,
    getUserNotificationsLoading: loading,
    getUserNotificationsData: data,
  };
}

export const {
  actionTypes: getUserNotificationsActionsTypes,
  actions: getUserNotificationsActions,
  reducer: getUserNotificationsReducer,
} = GetUserNotifications;
